/**
 * /suprimentos/sobras — Aproveitamento de Sobras (Plano de Suprimentos §6).
 *
 * Sobra é ativo próprio (D19/D24): tem código, produto de origem, medidas,
 * Box físico e estado (prevista/disponível/reservada/usada/descartada/
 * indisponível). O saldo da chapa inteira NÃO muda por aqui.
 *
 * Abas: Sobras (lista + ações por estado), Reservadas e Usadas (as mesmas
 * sobras, já filtradas pelo estado — pedido do dono, 2026-09-10), Previstas
 * (o Cut disse que sobraria — o almoxarife confirma), Boxes (onde as
 * sobras ficam), Exportações (histórico dos .xlsx do otimizador).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiDownload, FiPlus, FiRefreshCw, FiTrash2, FiUpload } from "react-icons/fi";

import ConfirmDialog from "../../../components/ConfirmDialog";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useRespostaMaisRecente, useValorComAtraso } from "../../../hooks/useBuscaRemota";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { listarProdutos } from "../../../services/estoque";
import {
  ESTADOS_SOBRA,
  excluirSobras,
  exportarSobrasXlsx,
  linhasDe,
  listarBoxes,
  listarSobras,
} from "../../../services/suprimentos";
import { mq } from "../../../styles/canonical/breakpoints";
import { nomeMaterial } from "../../../utils/nomeMaterial";
import { Card, ErrorMsg, SearchInput, SecondaryButton, Select } from "../styles";
import { AbaBtn, AbasWrap, AcoesLista, FiltrosLinha } from "./styles";
import BoxesTab from "./BoxesTab";
import ExportacoesTab from "./ExportacoesTab";
import ImportarSobrasPanel from "./ImportarSobrasPanel";
import NovaSobraPanel from "./NovaSobraPanel";
import PrevistasTab from "./PrevistasTab";
import SobraDetalhePanel from "./SobraDetalhePanel";
import TabelaSobras from "./TabelaSobras";
import { Carregando, Vazio, VazioFiltro } from "../_shared/Estados";

// Abas que são a lista de sobras com o estado já fixado.
const ESTADO_DA_ABA = { reservadas: "reservada", usadas: "usada" };

const ABAS = [
  { id: "sobras", label: "Sobras" },
  { id: "reservadas", label: "Reservadas" },
  { id: "usadas", label: "Usadas" },
  { id: "previstas", label: "Previstas" },
  { id: "boxes", label: "Boxes" },
  { id: "exportacoes", label: "Exportações" },
];

export default function Sobras() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();
  const pode = (p) => isMaster || hasAllPermissions([p]);
  const podeCriar = pode("sobra:create");
  const podeExportar = pode("sobra_exportacao:create");
  const podeExcluir = pode("sobra:disable");
  const podeImportar = pode("importacao:executar");
  const mobile = useMediaQuery(mq.mobile);

  const [aba, setAba] = useState("sobras");
  const [sobras, setSobras] = useState([]);
  // Lista SEM filtro, só para os KPIs: a aba Usadas não pode zerar "Disponíveis".
  const [todas, setTodas] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({ estado: "", produto: "", box: "", busca: "" });
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [novo, setNovo] = useState(false);
  const [importando, setImportando] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const [editando, setEditando] = useState(null);
  const [versaoDetalhe, setVersaoDetalhe] = useState(0);
  const [detalheId, setDetalheId] = useState(null);
  const estadoFixo = ESTADO_DA_ABA[aba];
  const listaDeSobras = aba === "sobras" || Boolean(estadoFixo);
  // A busca vai ao servidor: espera a pessoa parar de digitar e descarta a
  // resposta de uma busca já superada (digitar "marul" mostrava "mar").
  const busca = useValorComAtraso(filtros.busca.trim());
  const iniciarCarga = useRespostaMaisRecente();

  const carregarBoxes = useCallback(async () => {
    try {
      setBoxes(linhasDe(await listarBoxes(request)));
    } catch (err) {
      setErro(err?.message || "Erro ao carregar boxes.");
    }
  }, [request]);

  const { estado, produto, box } = filtros;
  const carregar = useCallback(async () => {
    const vale = iniciarCarga();
    setLoading(true);
    try {
      const params = { estado: estadoFixo || estado, produto, box, busca };
      const [filtradas, completas] = await Promise.all([
        listarSobras(request, params),
        listarSobras(request, {}),
      ]);
      if (!vale()) return;
      setSobras(linhasDe(filtradas));
      setTodas(linhasDe(completas));
      setErro(null);
    } catch (err) {
      if (!vale()) return;
      setErro(err?.payload?.detalhe || err?.message || "Erro ao carregar sobras.");
    } finally {
      if (vale()) setLoading(false);
    }
  // `filtros.busca` fica de fora de propósito: quem dispara a carga é `busca`,
  // já com o atraso.
  }, [request, estado, produto, box, busca, estadoFixo, iniciarCarga]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarBoxes(); }, [carregarBoxes]);
  useEffect(() => {
    listarProdutos(request, { pageSize: 500, ativo: true, categoria: "CHAPA" })
      .then((p) => setProdutos(linhasDe(p)))
      .catch(() => setProdutos([]));
  }, [request]);

  const kpis = useMemo(() => {
    const por = (e) => todas.filter((s) => s.estado === e).length;
    const m2 = todas
      .filter((s) => s.estado === "disponivel")
      .reduce((acc, s) => acc + Number(s.area_m2 || 0), 0);
    return [
      { label: "Disponíveis", value: por("disponivel"), tone: "success" },
      { label: "m² disponíveis", value: m2.toFixed(2) },
      { label: "Reservadas", value: por("reservada"), tone: "info" },
      { label: "Usadas", value: por("usada"), tone: "neutral" },
      { label: "Previstas", value: por("prevista"), tone: por("prevista") ? "warning" : "neutral" },
    ];
  }, [todas]);

  const toggleSel = (id) => setSelecionadas((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // Cabeçalho: marca/desmarca tudo o que está na lista atual.
  const toggleTodos = (ids) => setSelecionadas(new Set(ids));

  const exportar = async () => {
    // Só o que o otimizador pode usar (disponível e oferecida); o resto da
    // seleção existe para excluir/editar.
    const ids = [...selecionadas].filter((id) => sobras.find((s) => s.id === id)?.oferecivel);
    if (!ids.length) {
      toast.error("Marque sobras disponíveis para o otimizador.");
      return;
    }
    try {
      const codigo = await exportarSobrasXlsx(ids);
      toast.success(codigo ? `Exportação ${codigo} gerada.` : "Exportação gerada.");
      setSelecionadas(new Set());
    } catch (err) {
      toast.error(err?.message || "Falha ao exportar.");
    }
  };

  const aposMudanca = async () => {
    await carregar();
    await carregarBoxes();
  };

  const excluirSelecionadas = async () => {
    const ids = [...selecionadas];
    try {
      const r = await excluirSobras(request, ids);
      if (r.apagadas) toast.success(`${r.apagadas} sobra(s) apagada(s).`);
      // UM resumo, nunca um toast por sobra (247 toasts em 11/09).
      if (r.recusadas.length) {
        const partes = [];
        if (r.resumo.usada) partes.push(`${r.resumo.usada} usada(s) em lote`);
        if (r.resumo.reservada) partes.push(`${r.resumo.reservada} reservada(s) por plano`);
        toast.error(`${r.recusadas.length} não apagada(s): ${partes.join(" · ")}. Libere a reserva ou desfaça o uso antes.`);
      }
      setSelecionadas(new Set());
      setConfirmarExclusao(false);
      await aposMudanca();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao apagar.");
    }
  };

  return (
    <>
      <EntityHeader
        title="Aproveitamento de Sobras"
        subtitle="Retalhos de chapa com código, medida e Box — o que o otimizador pode usar antes de abrir chapa nova."
        primaryAction={podeCriar ? {
          label: "Nova sobra", icon: <FiPlus size={14} />, onClick: () => setNovo(true),
        } : undefined}
        actions={[
          // Celular: importar sai da barra da lista e vai para o menu do
          // cabeçalho; a barra fica só com o que depende da seleção.
          ...(mobile && podeImportar ? [{ label: "Importar sobras", icon: <FiUpload size={14} />, onClick: () => setImportando(true) }] : []),
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: aposMudanca },
        ]}
      />
      <KpiStrip items={kpis} />

      <AbasWrap role="tablist">
        {ABAS.map((a) => (
          <AbaBtn key={a.id} type="button" role="tab" $on={aba === a.id} onClick={() => setAba(a.id)}>
            {a.label}
          </AbaBtn>
        ))}
      </AbasWrap>

      {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

      {listaDeSobras && (
        <Card>
          {/* Barra de ações da lista: importar, exportar e apagar a seleção.
              Visível (não escondida no menu "⋮"): é o dia a dia do almoxarife. */}
          <AcoesLista>
            {podeImportar && !mobile && (
              <SecondaryButton type="button" onClick={() => setImportando(true)} title="Importar a TabelaSobras (.xls/.xlsx)">
                <FiUpload size={13} /> Importar sobras
              </SecondaryButton>
            )}
            {podeExportar && (!mobile || selecionadas.size > 0) && (
              <SecondaryButton type="button" onClick={exportar} title="Gera o .xlsx das sobras marcadas para o otimizador">
                <FiDownload size={13} /> Exportar para o otimizador{selecionadas.size ? ` (${selecionadas.size})` : ""}
              </SecondaryButton>
            )}
            {podeExcluir && (!mobile || selecionadas.size > 0) && (
              <SecondaryButton
                type="button"
                onClick={() => setConfirmarExclusao(true)}
                disabled={selecionadas.size === 0}
                title={selecionadas.size ? `Apagar ${selecionadas.size} sobra(s) marcada(s)` : "Marque sobras na lista para apagar"}
                style={selecionadas.size ? { color: "var(--danger-text)", borderColor: "var(--danger-text)" } : undefined}
              >
                <FiTrash2 size={13} /> Excluir{selecionadas.size ? ` (${selecionadas.size})` : ""}
              </SecondaryButton>
            )}
          </AcoesLista>
          <FiltrosLinha>
            <SearchInput
              placeholder="Código da sobra ou material…"
              value={filtros.busca}
              onChange={(e) => setFiltros((p) => ({ ...p, busca: e.target.value }))}
            />
            {!estadoFixo && (
              <Select value={filtros.estado} onChange={(e) => setFiltros((p) => ({ ...p, estado: e.target.value }))}>
                <option value="">Todos os estados</option>
                {ESTADOS_SOBRA.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            )}
            <Select className="material" value={filtros.produto} onChange={(e) => setFiltros((p) => ({ ...p, produto: e.target.value }))}>
              <option value="">Todos os materiais</option>
              {produtos.map((p) => <option key={p.id} value={p.id}>{nomeMaterial(p)}</option>)}
            </Select>
            <Select value={filtros.box} onChange={(e) => setFiltros((p) => ({ ...p, box: e.target.value }))}>
              <option value="">Todos os boxes</option>
              {boxes.map((b) => <option key={b.id} value={b.id}>{b.codigo}</option>)}
            </Select>
          </FiltrosLinha>
          {loading ? (
            <Carregando linhas={6} colunas={7} />
          ) : sobras.length === 0 ? (
            estadoFixo === "reservada" ? (
              <Vazio titulo="Nenhuma sobra reservada" descricao="Quando um plano de corte reservar um retalho, ele aparece aqui." />
            ) : estadoFixo === "usada" ? (
              <Vazio titulo="Nenhuma sobra usada" descricao="Sobras consumidas num corte ficam registradas aqui, com o lote que as usou." />
            ) : filtros.busca || filtros.estado || filtros.produto || filtros.box ? (
              <VazioFiltro onLimpar={() => setFiltros({ estado: "", produto: "", box: "", busca: "" })} />
            ) : (
              <Vazio
                titulo="Nenhuma sobra cadastrada"
                descricao="Cadastre um retalho manualmente ou importe a TabelaSobras do gerenciador."
                acoes={[
                  ...(podeImportar ? [{ label: "Importar sobras", onClick: () => setImportando(true) }] : []),
                  ...(podeCriar ? [{ label: "Nova sobra", onClick: () => setNovo(true) }] : []),
                ]}
              />
            )
          ) : (
            <TabelaSobras
              sobras={sobras}
              selecionadas={selecionadas}
              onToggleSel={toggleSel}
              onToggleTodos={toggleTodos}
              onAbrir={setDetalheId}
              referencia={estadoFixo}
            />
          )}
        </Card>
      )}

      {aba === "previstas" && (
        <PrevistasTab boxes={boxes} onMudou={aposMudanca} />
      )}
      {aba === "boxes" && (
        <BoxesTab boxes={boxes} onMudou={carregarBoxes} />
      )}
      {aba === "exportacoes" && <ExportacoesTab />}

      <NovaSobraPanel
        open={novo || Boolean(editando)}
        sobra={editando}
        onClose={() => { setNovo(false); setEditando(null); }}
        produtos={produtos}
        boxes={boxes}
        onCriada={async () => {
          setNovo(false);
          setEditando(null);
          setVersaoDetalhe((v) => v + 1);
          await aposMudanca();
        }}
      />
      <ImportarSobrasPanel
        open={importando}
        onClose={() => setImportando(false)}
        onImportou={aposMudanca}
      />
      <ConfirmDialog
        open={confirmarExclusao}
        titulo={`Apagar ${selecionadas.size} sobra(s)?`}
        descricao="A sobra some do cadastro. Sobras usadas ou reservadas não são apagadas; para tirar do otimizador sem apagar, use Descartar."
        confirmarLabel="Apagar"
        onConfirmar={excluirSelecionadas}
        onCancelar={() => setConfirmarExclusao(false)}
      />
      <SobraDetalhePanel
        sobraId={detalheId}
        boxes={boxes}
        versao={versaoDetalhe}
        onClose={() => setDetalheId(null)}
        onMudou={aposMudanca}
        onEditar={setEditando}
      />
    </>
  );
}
